import React, { useMemo, useState } from 'react';

import { InventorySlotButton } from './components/InventorySlotButton';
import {
  ITEMS,
  type Item,
  type ItemType,
  getItemStackSize,
  RECIPES,
  type Recipe,
  type RecipeIngredient,
} from './constants';
import type {
  PlayerInventory as PlayerInventoryState,
  PlayerInventorySlot,
} from './GameState';
import { getPlayerInventoryItemCounts } from './GameState';
import {
  HandCraftingQueueEntry,
  calculateHandCraftingQueuedOutputs,
  planHandCraftingForRequirements,
} from './HandCrafting';
import { getUnlockedRecipes, type ResearchState } from './ResearchSystem';

interface PlayerInventoryProps {
  inventory: PlayerInventoryState;
  updateInventory: (
    updater: (inventory: PlayerInventoryState) => PlayerInventoryState
  ) => void;
  onClose: () => void;
  hotkeyLabel?: string;
  handCraftingQueue: HandCraftingQueueEntry[];
  onQueueHandCrafting: (itemType: ItemType, amount: number) => void;
  researchState: ResearchState;
}

type HeldStack = {
  itemType: ItemType;
  count: number;
};

interface CraftingOption {
  recipe: Recipe;
  output: RecipeIngredient;
  itemConfig: Item | null;
  inputsDescription: string;
  canCraft: boolean;
  outputCount: number;
}

const ITEM_CONFIG_BY_TYPE: Record<ItemType, Item> = Object.values(ITEMS).reduce(
  (acc, item) => {
    acc[item.type] = item;
    return acc;
  },
  {} as Record<ItemType, Item>
);

const createEmptySlot = (): PlayerInventorySlot => ({
  itemType: null,
  count: 0,
});

export const PlayerInventoryWindow: React.FC<PlayerInventoryProps> = ({
  inventory,
  updateInventory,
  onClose,
  hotkeyLabel = 'I / E',
  handCraftingQueue,
  onQueueHandCrafting,
  researchState,
}) => {
  const [heldStack, setHeldStack] = useState<HeldStack | null>(null);
  const [lastInteractionIndex, setLastInteractionIndex] = useState<
    number | null
  >(null);

  const normalizedInventory = useMemo(() => {
    if (!inventory || inventory.length === 0) {
      return [] as PlayerInventoryState;
    }
    return inventory.map(slot => ({ ...slot }));
  }, [inventory]);

  const playerCounts = useMemo(
    () => getPlayerInventoryItemCounts(inventory),
    [inventory]
  );

  const queuedOutputs = useMemo(
    () => calculateHandCraftingQueuedOutputs(handCraftingQueue),
    [handCraftingQueue]
  );

  const unlockedRecipeIds = useMemo(() => {
    return new Set(getUnlockedRecipes(researchState));
  }, [researchState]);

  const craftingOptions = useMemo(() => {
    return (Object.values(RECIPES) as Recipe[])
      .map((recipe: Recipe) => {
        if (!unlockedRecipeIds.has(recipe.id)) {
          return null;
        }

        const output: RecipeIngredient | undefined = recipe.outputs[0];
        if (!output) {
          return null;
        }

        const adjustedPlayerCounts: Record<ItemType, number> = {
          ...playerCounts,
        };
        const adjustedQueuedOutputs: Record<ItemType, number> = {
          ...queuedOutputs,
        };

        adjustedPlayerCounts[output.item] = 0;
        adjustedQueuedOutputs[output.item] = 0;

        const requirementAmount = Math.max(1, output.count);

        const plan = planHandCraftingForRequirements(
          [
            {
              item: output.item,
              amount: requirementAmount,
            },
          ],
          adjustedPlayerCounts,
          adjustedQueuedOutputs
        );

        const itemConfig = ITEM_CONFIG_BY_TYPE[output.item] ?? null;
        const inputsDescription =
          recipe.inputs.length > 0
            ? recipe.inputs
                .map((input: RecipeIngredient) => {
                  const inputConfig = ITEM_CONFIG_BY_TYPE[input.item] ?? null;
                  const inputName = inputConfig?.name ?? input.item;
                  return `${input.count} ${inputName}`;
                })
                .join(', ')
            : 'No ingredients';

        const option: CraftingOption = {
          recipe,
          output,
          itemConfig,
          inputsDescription,
          canCraft: Boolean(plan),
          outputCount: requirementAmount,
        };

        return option;
      })
      .filter((entry): entry is CraftingOption => entry !== null);
  }, [playerCounts, queuedOutputs, unlockedRecipeIds]);

  const handleQueueCraft = (option: CraftingOption) => {
    if (!option.canCraft) {
      return;
    }

    onQueueHandCrafting(option.output.item, option.outputCount);
  };

  const handlePickUpSlot = (index: number, slot: PlayerInventorySlot) => {
    if (!slot.itemType || slot.count <= 0) {
      return;
    }

    setHeldStack({ itemType: slot.itemType, count: slot.count });
    setLastInteractionIndex(index);

    updateInventory(prev => {
      const next = prev.map(existing => ({ ...existing }));
      if (!next[index]) {
        return prev;
      }
      next[index] = createEmptySlot();
      return next;
    });
  };

  const handlePlaceIntoEmpty = (index: number) => {
    if (!heldStack) {
      return;
    }

    const stackToPlace = heldStack;
    setLastInteractionIndex(index);

    updateInventory(prev => {
      const next = prev.map(existing => ({ ...existing }));
      if (!next[index]) {
        return prev;
      }
      next[index] = { ...stackToPlace };
      return next;
    });

    setHeldStack(null);
  };

  const handleSwapStacks = (
    index: number,
    _targetSlot: PlayerInventorySlot,
    stackToPlace: HeldStack
  ) => {
    let swapped: HeldStack | null = null;

    updateInventory(prev => {
      const next = prev.map(existing => ({ ...existing }));
      const currentTarget = next[index];
      if (!currentTarget) {
        return prev;
      }

      if (currentTarget.itemType && currentTarget.count > 0) {
        swapped = {
          itemType: currentTarget.itemType,
          count: currentTarget.count,
        };
      } else {
        swapped = null;
      }

      next[index] = { ...stackToPlace };
      return next;
    });

    setLastInteractionIndex(index);
    if (swapped) {
      setHeldStack(swapped);
    } else {
      setHeldStack(null);
    }
  };

  const handleMergeStacks = (index: number) => {
    if (!heldStack) {
      return;
    }

    let remainder = heldStack.count;
    updateInventory(prev => {
      const next = prev.map(existing => ({ ...existing }));
      const currentTarget = next[index];
      if (!currentTarget) {
        return prev;
      }
      if (currentTarget.itemType !== heldStack.itemType) {
        return prev;
      }

      const stackSize = getItemStackSize(heldStack.itemType);
      const availableSpace = stackSize - currentTarget.count;
      if (availableSpace <= 0) {
        remainder = heldStack.count;
        return prev;
      }

      const toTransfer = Math.min(availableSpace, heldStack.count);
      remainder = heldStack.count - toTransfer;
      next[index] = {
        itemType: heldStack.itemType,
        count: currentTarget.count + toTransfer,
      };
      return next;
    });

    setLastInteractionIndex(index);
    if (remainder > 0) {
      setHeldStack({ itemType: heldStack.itemType, count: remainder });
    } else {
      setHeldStack(null);
    }
  };

  const handleLeftClick = (index: number) => {
    const slot = normalizedInventory[index];
    if (!slot) {
      return;
    }

    if (!heldStack) {
      handlePickUpSlot(index, slot);
      return;
    }

    if (!slot.itemType || slot.count <= 0) {
      handlePlaceIntoEmpty(index);
      return;
    }

    if (slot.itemType === heldStack.itemType) {
      handleMergeStacks(index);
      return;
    }

    handleSwapStacks(index, slot, heldStack);
  };

  const handleRightClick = (index: number, event: React.MouseEvent) => {
    event.preventDefault();
    const slot = normalizedInventory[index];
    if (!slot) {
      return;
    }

    setLastInteractionIndex(index);

    if (!heldStack) {
      if (!slot.itemType || slot.count <= 0) {
        return;
      }

      const half = Math.ceil(slot.count / 2);
      const remaining = slot.count - half;

      setHeldStack({ itemType: slot.itemType, count: half });

      updateInventory(prev => {
        const next = prev.map(existing => ({ ...existing }));
        if (!next[index]) {
          return prev;
        }
        next[index] =
          remaining > 0
            ? { itemType: slot.itemType, count: remaining }
            : createEmptySlot();
        return next;
      });
      return;
    }

    if (slot.itemType && slot.itemType !== heldStack.itemType) {
      return;
    }

    let placed = 0;
    updateInventory(prev => {
      const next = prev.map(existing => ({ ...existing }));
      const currentTarget = next[index];
      if (!currentTarget) {
        return prev;
      }
      const currentCount =
        currentTarget.itemType === heldStack.itemType ? currentTarget.count : 0;
      const stackSize = getItemStackSize(heldStack.itemType);

      if (currentCount >= stackSize) {
        return prev;
      }

      placed = 1;
      next[index] = {
        itemType: heldStack.itemType,
        count: currentCount + 1,
      };
      return next;
    });

    if (placed === 0) {
      return;
    }

    const remaining = heldStack.count - placed;
    if (remaining > 0) {
      setHeldStack({ itemType: heldStack.itemType, count: remaining });
    } else {
      setHeldStack(null);
    }
  };

  const hotkeyDisplay = hotkeyLabel.toUpperCase();

  const heldStackConfig = heldStack
    ? ITEM_CONFIG_BY_TYPE[heldStack.itemType]
    : undefined;
  const heldStackName =
    heldStackConfig?.name ?? heldStack?.itemType ?? 'Unknown item';

  return (
    <div
      className='inventory-window'
      role='dialog'
      aria-modal='false'
      aria-label='Player inventory'
    >
      <div className='inventory-window-header'>
        <div className='inventory-window-title-group'>
          <span className='inventory-window-title'>Inventory</span>
          <span className='inventory-window-hotkey'>
            Press {hotkeyDisplay} to toggle
          </span>
        </div>
        <button
          type='button'
          className='inventory-close-button'
          onClick={onClose}
          aria-label='Close inventory'
        >
          ×
        </button>
      </div>
      <div className='hand-crafting-menu'>
        <div className='hand-crafting-menu-header'>
          <span className='hand-crafting-menu-title'>Hand Crafting</span>
          <span className='hand-crafting-menu-description'>
            Click an item to queue manual crafting. Intermediate components are
            crafted automatically when resources allow.
          </span>
        </div>
        <div className='hand-crafting-list'>
          {craftingOptions.map(option => {
            const itemName = option.itemConfig?.name ?? option.output.item;
            const buttonClasses = [
              'hand-crafting-button',
              option.canCraft ? '' : 'hand-crafting-button--disabled',
            ]
              .filter(Boolean)
              .join(' ');
            const statusLabel = option.canCraft
              ? 'Craft'
              : 'Missing raw resources';

            return (
              <button
                key={option.recipe.id}
                type='button'
                className={buttonClasses}
                onClick={() => handleQueueCraft(option)}
                disabled={!option.canCraft}
                title={
                  option.canCraft
                    ? `Craft ${itemName}`
                    : `Cannot craft ${itemName}: insufficient raw resources`
                }
              >
                <div className='hand-crafting-item-header'>
                  <span className='hand-crafting-item-icon'>
                    {option.itemConfig?.character ?? '?'}
                  </span>
                  <span className='hand-crafting-item-name'>
                    {option.recipe.name}
                  </span>
                  <span className='hand-crafting-item-output'>
                    ×{option.outputCount}
                  </span>
                </div>
                <div className='hand-crafting-item-inputs'>
                  Requires: {option.inputsDescription}
                </div>
                <div
                  className={`hand-crafting-item-status${
                    option.canCraft ? '' : ' hand-crafting-item-status--missing'
                  }`}
                >
                  {statusLabel}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className={`held-stack${heldStack ? '' : ' empty'}`}>
        {heldStack ? `${heldStackName}: ${heldStack.count}` : 'No items held'}
      </div>
      <div className='player-inventory'>
        <div className='inventory-grid'>
          {normalizedInventory.map((slot, index) => {
            const isEmpty = !slot.itemType || slot.count <= 0;
            const isFull =
              slot.itemType != null &&
              slot.count >= getItemStackSize(slot.itemType);
            const isActive = lastInteractionIndex === index;
            const isCompatible =
              heldStack != null &&
              (isEmpty || slot.itemType === heldStack.itemType);
            const progress =
              !isEmpty && slot.itemType
                ? Math.min(slot.count / getItemStackSize(slot.itemType), 1)
                : 0;
            const itemConfig = slot.itemType
              ? ITEM_CONFIG_BY_TYPE[slot.itemType]
              : null;
            const slotClasses = [
              isEmpty ? 'empty' : 'filled',
              isFull ? 'full' : '',
              isActive ? 'active' : '',
              isCompatible ? 'compatible' : '',
            ]
              .filter(Boolean)
              .join(' ');

            const capacityLabel = slot.itemType
              ? `${slot.count} / ${getItemStackSize(slot.itemType)}`
              : 'Empty';

            const title = slot.itemType
              ? `${itemConfig?.name ?? slot.itemType} — ${capacityLabel}`
              : 'Empty slot';

            return (
              <InventorySlotButton
                key={index}
                className={slotClasses}
                indexLabel={index + 1}
                icon={itemConfig ? itemConfig.character : '·'}
                name={itemConfig ? itemConfig.name : 'Empty'}
                countLabel={capacityLabel}
                capacityLabel={
                  itemConfig
                    ? `Stack: ${getItemStackSize(itemConfig.type)}`
                    : ''
                }
                typeLabel={itemConfig ? itemConfig.type : ''}
                progress={progress}
                title={title}
                ariaLabel={`Inventory slot ${index + 1}: ${title}`}
                onClick={() => handleLeftClick(index)}
                onContextMenu={event => handleRightClick(index, event)}
              />
            );
          })}
        </div>
        <p className='inventory-help-text'>
          Left click to pick up or place stacks. Right click to split a stack or
          deposit a single item. Press {hotkeyDisplay} or the backpack button to
          close.
        </p>
      </div>
    </div>
  );
};

export default PlayerInventoryWindow;
